// frontend/src/pages/PlotDetail.js
import React from 'react';
import { useParams } from 'react-router-dom';
import { useDeviceProvisioning } from '../hooks/useDeviceProvisioning';
import { PlotDetailProvider, usePlotDetailContext } from '../context/PlotDetailContext';
import PlotHeader from '../components/plot/PlotHeader';
import DeviceList from '../components/plot/DeviceList';
import DisplayControls from '../components/plot/DisplayControls';
import { MetricCategoryPanel } from '../components/plot/MetricCharts';

const CATEGORY_TITLES = {
  weather: 'Environmental Conditions',
  realtime: 'Realtime Conditions',
  crop: 'Accumulation Models & Phenology Stages',
  custom: 'Environmental & Custom'
};

function PlotDetailContent() {
  const { farmId, plotId } = useParams();
  const {
    plot,
    plotData,
    devices, // Use normalized devices from PlotDetailContext
    loading,
    error,
    chartData,
    categoryGroups,
    visibleDeviceIds,
    visibleDevices,
    activeMetrics,
    toggleDevice,
    toggleMetric,
    localTimeEnabled,
    forecastVisible,
    disabledSections,
    fetchPlotDetails
  } = usePlotDetailContext();

  const { openAddDeviceModal, DeviceProvisioningModal } = useDeviceProvisioning(async () => {
    if (fetchPlotDetails) {
      await fetchPlotDetails();
    }
  });

  const activePlot = plotData || plot;

  if (loading) {
    return <div style={{ padding: '32px', textAlign: 'center' }}>Loading plot details...</div>;
  }

  if (error || !activePlot) {
    return <div style={{ padding: '32px', color: '#dc2626' }}>{error || 'Plot not found'}</div>;
  }

  const allAvailableMetrics = Object.values(categoryGroups)
    .flat()
    .map((m) => m.type);

  const effectiveActiveMetrics = activeMetrics !== undefined && activeMetrics !== null
    ? activeMetrics
    : allAvailableMetrics;

  return (
    <div className="plot-detail-page" style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh', fontFamily: 'sans-serif' }}>

      {/* Plot Metadata & Device List */}
      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
        <PlotHeader
          metadata={activePlot.metadata || activePlot} plotId={plotId}
          farmId={farmId} />
        <DeviceList
          devices={devices} // Pass normalized context devices explicitly
          visibleDeviceIds={visibleDeviceIds}
          onToggleDevice={toggleDevice}
          onAddDevice={(targetPlot) => openAddDeviceModal(targetPlot || activePlot)}
          onOpenModal={() => openAddDeviceModal(activePlot)}
        />
      </div>

      {/* Dynamic Display Controls */}
      <DisplayControls
        categoryGroups={categoryGroups}
        activeMetrics={effectiveActiveMetrics}
        onToggleMetric={toggleMetric}
      />

      {/* Metric Charts */}
      {Object.keys(categoryGroups).map((catKey) => {
        if (disabledSections[catKey]) return null;

        const catMetrics = (categoryGroups[catKey] || []).filter((m) => effectiveActiveMetrics.includes(m.type));
        if (!catMetrics || catMetrics.length === 0) return null;

        const categoryTitle = CATEGORY_TITLES[catKey] || catKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

        return (
          <MetricCategoryPanel
            key={catKey}
            categoryKey={catKey}
            title={categoryTitle}
            metrics={catMetrics}
            chartData={chartData}
            visibleDevices={visibleDevices}
            forecastVisible={forecastVisible}
            localTimeEnabled={localTimeEnabled}
          />
        );
      })}

      <DeviceProvisioningModal />
    </div>
  );
}

export default function PlotDetail() {
  const { farmId, plotId } = useParams();

  return (
    <PlotDetailProvider farmId={farmId} plotId={plotId}>
      <PlotDetailContent />
    </PlotDetailProvider>
  );
}
